import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Mail } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Label } from './Label';
import { Card, CardContent } from './Card';
export function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };
  return <section id="contact" className="py-20 bg-muted/30" data-id="element-25">
      <div className="container mx-auto px-4" data-id="element-26">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-27">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-foreground" data-id="element-28">
            Schedule Your Free Consultation
          </h2>
          <p className="text-lg text-muted-foreground" data-id="element-29">
            Take the first step towards a pain-free life. Fill out the form
            below or call us directly to schedule your comprehensive neuropathy
            evaluation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12" data-id="element-30">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-31">
            <Card className="border-muted/50 shadow-lg" data-id="element-32">
              <CardContent className="p-8" data-id="element-33">
                {isSubmitted ? <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center gap-4" data-id="element-34">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-35">
                      <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-36">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-37" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold" data-id="element-38">Request Received!</h3>
                    <p className="text-muted-foreground" data-id="element-39">
                      Thank you for reaching out. A member of our team will
                      contact you shortly to confirm your consultation time.
                    </p>
                    <Button className="mt-4" onClick={() => setIsSubmitted(false)} variant="outline" data-id="element-40">
                      Send Another Message
                    </Button>
                  </div> : <form onSubmit={handleSubmit} className="flex flex-col gap-6" data-id="element-41">
                    <div className="grid sm:grid-cols-2 gap-6" data-id="element-42">
                      <div className="flex flex-col gap-2" data-id="element-43">
                        <Label htmlFor="firstName" data-id="element-44">First Name</Label>
                        <Input id="firstName" required placeholder="John" data-id="element-45" />
                      </div>
                      <div className="flex flex-col gap-2" data-id="element-46">
                        <Label htmlFor="lastName" data-id="element-47">Last Name</Label>
                        <Input id="lastName" required placeholder="Doe" data-id="element-48" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-2" data-id="element-49">
                      <Label htmlFor="phone" data-id="element-50">Phone Number</Label>
                      <Input id="phone" type="tel" required placeholder="(904) 123-4567" data-id="element-51" />
                    </div>
                    <div className="flex flex-col gap-2" data-id="element-52">
                      <Label htmlFor="email" data-id="element-53">Email Address</Label>
                      <Input id="email" type="email" required placeholder="john@example.com" data-id="element-54" />
                    </div>
                    <div className="flex flex-col gap-2" data-id="element-55">
                      <Label htmlFor="message" data-id="element-56">Message (Optional)</Label>
                      <Textarea id="message" placeholder="Tell us briefly about your symptoms..." className="min-h-[100px]" data-id="element-57" />
                    </div>
                    <Button type="submit" size="lg" className="w-full text-base h-12" data-id="element-58">
                      Request Appointment
                    </Button>
                  </form>}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="flex flex-col gap-8" data-id="element-59">
            <div className="grid sm:grid-cols-2 gap-8" data-id="element-60">
              <div className="flex gap-4" data-id="element-61">
                <MapPin className="w-6 h-6 text-primary flex-shrink-0" data-id="element-62" />
                <div data-id="element-63">
                  <h4 className="font-bold mb-1" data-id="element-64">Location</h4>
                  <p className="text-muted-foreground" data-id="element-65">
                    1 St. Johns Medical Park Dr.
                    <br data-id="element-66" />
                    Suite 2<br data-id="element-67" />
                    St. Augustine, FL 32086
                  </p>
                </div>
              </div>
              <div className="flex gap-4" data-id="element-68">
                <Phone className="w-6 h-6 text-primary flex-shrink-0" data-id="element-69" />
                <div data-id="element-70">
                  <h4 className="font-bold mb-1" data-id="element-71">Contact</h4>
                  <p className="text-muted-foreground" data-id="element-72">
                    <a href="tel:9041234567" className="hover:text-primary transition-colors" data-id="element-73">
                      (904) 123-4567
                    </a>
                    <br data-id="element-74" />
                    <a href="mailto:info@neuropathyhealingctr.com" className="hover:text-primary transition-colors" data-id="element-75">
                      info@neuropathyhealingctr.com
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex gap-4 sm:col-span-2" data-id="element-76">
                <Clock className="w-6 h-6 text-primary flex-shrink-0" data-id="element-77" />
                <div data-id="element-78">
                  <h4 className="font-bold mb-1" data-id="element-79">Hours</h4>
                  <p className="text-muted-foreground" data-id="element-80">
                    Monday - Thursday: 9:00 AM - 5:00 PM
                    <br data-id="element-81" />
                    Friday: 9:00 AM - 1:00 PM
                    <br data-id="element-82" />
                    Saturday - Sunday: Closed
                  </p>
                </div>
              </div>
            </div>

            <div className="flex-grow rounded-2xl overflow-hidden shadow-lg min-h-[300px] bg-muted relative" data-id="element-83">
              <iframe width="100%" height="100%" style={{
              border: 0
            }} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" src="https://www.openstreetmap.org/export/embed.html?bbox=-81.335%2C29.835%2C-81.315%2C29.855&amp;layer=mapnik&amp;marker=29.845%2C-81.325" title="Map showing clinic location" className="absolute inset-0" data-id="element-84"></iframe>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}